import React from 'react';

const HEADING_FONT = 'Manrope, sans-serif';
const BODY_FONT = 'Inter, sans-serif';

const DEFAULT_FIELDS = {
  contact: [
    { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Your full name' },
    { name: 'email', label: 'Email', type: 'email', required: true, placeholder: 'you@example.com' },
    { name: 'phone', label: 'Phone', type: 'tel', required: false, placeholder: '+1 (555) 000-0000' },
    { name: 'message', label: 'Message', type: 'textarea', required: false, placeholder: 'How can we help?' },
  ],
  newsletter: [
    { name: 'email', label: 'Email', type: 'email', required: true, placeholder: 'you@example.com' },
  ],
  booking_request: [
    { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Your full name' },
    { name: 'email', label: 'Email', type: 'email', required: true, placeholder: 'you@example.com' },
    { name: 'phone', label: 'Phone', type: 'tel', required: false, placeholder: '+1 (555) 000-0000' },
    { name: 'company', label: 'Company', type: 'text', required: false, placeholder: 'Your company' },
    { name: 'preferred_date', label: 'Preferred Date', type: 'date', required: false, placeholder: '' },
    { name: 'message', label: 'Message', type: 'textarea', required: false, placeholder: 'Tell us about your needs' },
  ],
};

function getFields(props, variant) {
  if (props.fields && props.fields.length > 0) {
    return props.fields;
  }
  return DEFAULT_FIELDS[variant] || DEFAULT_FIELDS.contact;
}

function FormField({ field }) {
  const baseInputClasses =
    'w-full px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-800 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-400 focus:border-transparent transition-colors';

  return (
    <div className="space-y-1.5">
      <label
        className="block text-sm font-medium text-stone-700"
        style={{ fontFamily: BODY_FONT }}
      >
        {field.label}
        {field.required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {field.type === 'textarea' ? (
        <textarea
          className={`${baseInputClasses} min-h-[100px] resize-none`}
          placeholder={field.placeholder}
          readOnly
        />
      ) : (
        <input
          type={field.type || 'text'}
          className={baseInputClasses}
          placeholder={field.placeholder}
          readOnly
        />
      )}
    </div>
  );
}

/**
 * Form Block Renderer
 * Variants: contact, newsletter, booking_request, custom
 */
export default function FormRenderer({ props, variant }) {
  const {
    headline = 'Get in Touch',
    subheadline = '',
    submit_text = 'Submit',
    success_message = 'Thanks! We\'ll be in touch soon.',
    background_color = '#1c1917',
    text_color = '#ffffff',
    form_style = 'card',
  } = props;

  const fields = getFields(props, variant);

  const headerEl = (headline || subheadline) ? (
    <div className={form_style === 'inline' ? 'mb-4' : 'mb-6'}>
      {headline && (
        <h2
          className={`font-bold text-stone-900 ${form_style === 'minimal' ? 'text-xl' : 'text-2xl sm:text-3xl'} mb-2`}
          style={{ fontFamily: HEADING_FONT }}
        >
          {headline}
        </h2>
      )}
      {subheadline && (
        <p
          className="text-stone-500 text-sm sm:text-base"
          style={{ fontFamily: BODY_FONT }}
        >
          {subheadline}
        </p>
      )}
    </div>
  ) : null;

  const submitButton = (
    <button
      type="button"
      className="w-full px-6 py-3 rounded-lg font-semibold text-sm transition-opacity hover:opacity-90"
      style={{
        backgroundColor: background_color,
        color: text_color,
        fontFamily: BODY_FONT,
      }}
    >
      {submit_text}
    </button>
  );

  // Card style (default)
  if (form_style === 'card' || !form_style) {
    return (
      <section className="py-16 px-6" style={{ backgroundColor: '#FAFAF5' }}>
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-8">
            {headerEl}
            <div className="space-y-4">
              {fields.map((field, i) => (
                <FormField key={field.name || i} field={field} />
              ))}
              <div className="pt-2">{submitButton}</div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Inline style (horizontal for simple forms)
  if (form_style === 'inline') {
    const isSimple = fields.length <= 2 && !fields.some((f) => f.type === 'textarea');
    return (
      <section className="py-12 px-6" style={{ backgroundColor: '#FAFAF5' }}>
        <div className="max-w-3xl mx-auto text-center">
          {headerEl}
          {isSimple ? (
            <div className="flex gap-3 items-end justify-center">
              {fields.map((field, i) => (
                <div key={field.name || i} className="flex-1 max-w-xs">
                  <FormField field={field} />
                </div>
              ))}
              <div className="flex-shrink-0">
                <button
                  type="button"
                  className="px-6 py-2.5 rounded-lg font-semibold text-sm transition-opacity hover:opacity-90"
                  style={{
                    backgroundColor: background_color,
                    color: text_color,
                    fontFamily: BODY_FONT,
                  }}
                >
                  {submit_text}
                </button>
              </div>
            </div>
          ) : (
            <div className="max-w-lg mx-auto space-y-4">
              {fields.map((field, i) => (
                <FormField key={field.name || i} field={field} />
              ))}
              <div className="pt-2">{submitButton}</div>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Minimal style
  if (form_style === 'minimal') {
    return (
      <section className="py-12 px-6" style={{ backgroundColor: '#FAFAF5' }}>
        <div className="max-w-md mx-auto">
          {headerEl}
          <div className="space-y-3">
            {fields.map((field, i) => (
              <FormField key={field.name || i} field={field} />
            ))}
            <div className="pt-1">{submitButton}</div>
          </div>
        </div>
      </section>
    );
  }

  // Fallback to card
  return (
    <section className="py-16 px-6" style={{ backgroundColor: '#FAFAF5' }}>
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-8">
          {headerEl}
          <div className="space-y-4">
            {fields.map((field, i) => (
              <FormField key={field.name || i} field={field} />
            ))}
            <div className="pt-2">{submitButton}</div>
          </div>
        </div>
      </div>
    </section>
  );
}
